import type { ISODateString, Nullable, UUID } from './common';
import type { BreakdownSnapshot } from './breakdown-snapshot';
import type { BreakdownSlotKey } from './job';
// Type-only: schemas/breakdown imports LINE_ITEM_CATEGORIES from this file, so a
// value import here would be a runtime cycle. `import type` is erased.
import type { BreakdownDocumentContents } from '../schemas/breakdown';

// Line item categories - matching common template categories
export const LINE_ITEM_CATEGORIES = [
  'Leak Detect',
  'Roofing',
  'Specialty',
  'Time and Materials',
  'Materials',
  'Adjustment',
  'Admin',
  'Surface Protection',
  'Management',
  'Disclaimer',
] as const;
export type LineItemCategory = (typeof LINE_ITEM_CATEGORIES)[number];

// Base sub-item interface (for qty-rate types with hours/rate)
export interface LaborSubItem {
  id: UUID;
  lineItemId: UUID;
  type: string;
  hours: number;
  rate: number;
  rateType?: Nullable<'hourly' | 'daily'>;
  unitKey?: Nullable<string>;
  amount?: Nullable<number>;
  note?: Nullable<string>;
  markupRate?: Nullable<number>;
  sortOrder: number;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

// Direct sub-item interface (for non-qty-rate types with direct amount)
export interface DirectSubItem {
  id: UUID;
  lineItemId: UUID;
  type: string;
  hours?: Nullable<number>;
  rate?: Nullable<number>;
  unitKey?: Nullable<string>;
  amount: number;
  note?: Nullable<string>;
  markupRate?: Nullable<number>;
  sortOrder: number;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export type BreakdownSubItem = LaborSubItem | DirectSubItem;

// Line item interface
export interface BreakdownLineItem {
  id: UUID;
  jobId: UUID;
  projectId?: Nullable<UUID>;
  category: string;
  description?: Nullable<string>;
  sortOrder: number;
  totalCost: number;
  templateId?: Nullable<string>;
  itemDate?: Nullable<string>;
  // Whether this line shows its sub-items on the invoice PDF. Toggled per-line
  // in the partial-invoice picker; persisted so a later FINAL invoice keeps it.
  itemized: boolean;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

// Line item with sub-items
export interface BreakdownLineItemWithSubItems extends BreakdownLineItem {
  subItems: BreakdownSubItem[];
}

// ============================================================================
// Breakdown Projects
// ============================================================================

export interface BreakdownProject {
  id: UUID;
  jobId: UUID;
  name: string;
  sortOrder: number;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface BreakdownProjectWithLineItems extends BreakdownProject {
  lineItems: BreakdownLineItemWithSubItems[];
}

// Full breakdown for a job
export interface JobBreakdown {
  lineItems: BreakdownLineItemWithSubItems[];  // loose items (no project)
  projects: BreakdownProjectWithLineItems[];
  totalCost: number;
  taxRate: number;
  markupRate: number;
  roundingPrecision?: number | null;
  // Present on the flag-on document read; identifies the active breakdown
  // document so callers can target per-line PATCH endpoints (e.g. itemized).
  documentId?: string | null;
}

// Breakdown version (snapshot)
export interface BreakdownVersion {
  id: UUID;
  jobId: UUID;
  name: string;
  tag?: Nullable<string>;
  snapshot: JobBreakdown;
  totalCost: number;
  createdBy: UUID;
  createdAt: ISODateString;
  slotKey: BreakdownSlotKey;
}

// Breakdown version with user info
export interface BreakdownVersionWithUser extends BreakdownVersion {
  createdByUser: {
    id: UUID;
    name: string;
    email: string;
  };
}

// ============================================================================
// Breakdown Documents (word-processor model)
// ============================================================================

// A single editable breakdown document. Belongs to a (jobId, slotKey) pair;
// names are unique within that pair. Independent of breakdownVersions —
// this is the new model where each saved version is its own editable doc.
//
// taxRate/markupRate/roundingPrecision are PER-DOCUMENT (not per-job). A job's
// rates are only the seed for a brand-new document; editing the rates on one
// draft does not affect any other draft for the same job.
export interface BreakdownDocument {
  id: UUID;
  jobId: UUID;
  slotKey: BreakdownSlotKey;
  name: string;
  tag?: Nullable<string>;
  taxRate: number;
  markupRate: number;
  roundingPrecision?: Nullable<number>;
  totalCost: number;
  createdBy: UUID;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface BreakdownDocumentWithUser extends BreakdownDocument {
  createdByName?: Nullable<string>;
}

// List-row shape: includes the Active marker for the document's (jobId, slotKey).
export interface BreakdownDocumentSummary extends BreakdownDocumentWithUser {
  isActive: boolean;
}

// Input types for creating/updating

export interface BreakdownSubItemInput {
  type: string;
  hours?: number;
  rate?: number;
  unitKey?: string;
  amount?: number;
  note?: string;
  markupRate?: number;
  sortOrder?: number;
}

export interface BreakdownSubItemUpdateInput extends Partial<BreakdownSubItemInput> {
  id?: UUID;
}

export interface BreakdownLineItemInput {
  category: string;
  description?: string;
  sortOrder?: number;
  templateId?: string;
  itemDate?: string;
  subItems?: BreakdownSubItemInput[];
}

export interface BreakdownLineItemUpdateInput extends Partial<BreakdownLineItemInput> {
  id?: UUID;
}

// Bulk save input - for saving entire breakdown at once
export interface BulkBreakdownInput {
  lineItems: Array<{
    id?: UUID; // If provided, update existing; if not, create new
    category: string;
    description?: string;
    sortOrder: number;
    templateId?: string;
    itemDate?: string;
    subItems: Array<{
      id?: UUID;
      type: string;
      hours?: number;
      rate?: number;
      unitKey?: string;
      amount?: number;
      note?: string;
      markupRate?: number;
      sortOrder: number;
    }>;
  }>;
}

// Reorder input
export interface ReorderInput {
  items: Array<{
    id: UUID;
    sortOrder: number;
  }>;
}

// Version input
export interface BreakdownVersionInput {
  name: string;
  tag?: Nullable<string>;
}

export interface BreakdownVersionUpdateInput {
  name: string;
  tag?: Nullable<string>;
}

// Default rates (used as fallbacks only - prefer fetching from API)
export const DEFAULT_TAX_RATE = 0.30;
export const DEFAULT_MARKUP_RATE = 0.145;

// Cost breakdown structure
export interface CostBreakdown {
  categoryTotals: Record<string, number>;
  tax: number;
  subtotal: number;
  markup: number;
  jobMarkup: number;
  customMarkup: number;
  profit: number;
  discount: number;
  total: number;
  rounding: number;
  roundedTotal: number;
}

// ============================================================================
// Patch-based saving types
// ============================================================================

// Patch for updating a line item field
export interface LineItemFieldPatch {
  op: 'updateLineItem';
  id: UUID;
  field: 'category' | 'description' | 'sortOrder' | 'templateId' | 'itemDate';
  value: string | number | null;
}

// Patch for updating a sub-item field
export interface SubItemFieldPatch {
  op: 'updateSubItem';
  id: UUID;
  lineItemId: UUID;
  field: 'type' | 'hours' | 'rate' | 'rateType' | 'unitKey' | 'amount' | 'note' | 'sortOrder' | 'markupRate';
  value: string | number | null;
}

// Patch for creating a new line item
export interface CreateLineItemPatch {
  op: 'createLineItem';
  id: UUID; // Client-generated UUID
  data: {
    category: string;
    description?: string;
    sortOrder: number;
    templateId?: string;
    itemDate?: string;
    projectId?: UUID | null;
  };
}

// Patch for creating a new sub-item
export interface CreateSubItemPatch {
  op: 'createSubItem';
  id: UUID; // Client-generated UUID
  lineItemId: UUID; // Parent line item UUID
  data: {
    type: string;
    hours?: number;
    rate?: number;
    rateType?: 'hourly' | 'daily';
    unitKey?: string;
    amount?: number;
    note?: string;
    markupRate?: number;
    sortOrder: number;
  };
}

// Patch for deleting a line item
export interface DeleteLineItemPatch {
  op: 'deleteLineItem';
  id: UUID;
}

// Patch for deleting a sub-item
export interface DeleteSubItemPatch {
  op: 'deleteSubItem';
  id: UUID;
  lineItemId: UUID;
}

// Patch for updating per-job rates
export interface UpdateRatesPatch {
  op: 'updateRates';
  taxRate?: number;
  markupRate?: number;
  roundingPrecision?: number | null;
}

// Patch for creating a project
export interface CreateProjectPatch {
  op: 'createProject';
  id: UUID;
  data: {
    name: string;
    sortOrder: number;
  };
}

// Patch for updating a project field
export interface UpdateProjectPatch {
  op: 'updateProject';
  id: UUID;
  field: 'name' | 'sortOrder';
  value: string | number;
}

// Patch for deleting a project (line items become loose)
export interface DeleteProjectPatch {
  op: 'deleteProject';
  id: UUID;
}

// Patch for moving a line item to/from/between projects
export interface MoveLineItemPatch {
  op: 'moveLineItem';
  id: UUID;
  projectId: UUID | null;
  sortOrder: number;
}

// Union type for all patches
export type BreakdownPatch =
  | LineItemFieldPatch
  | SubItemFieldPatch
  | CreateLineItemPatch
  | CreateSubItemPatch
  | DeleteLineItemPatch
  | DeleteSubItemPatch
  | UpdateRatesPatch
  | CreateProjectPatch
  | UpdateProjectPatch
  | DeleteProjectPatch
  | MoveLineItemPatch;

// Input for patch-based save
export interface PatchBreakdownInput {
  patches: BreakdownPatch[];
}

// Response from patch-based save
export interface PatchBreakdownResponse {
  success: boolean;
}

// Error response for duplicate ID
export interface PatchBreakdownDuplicateError {
  error: 'DUPLICATE_ID';
  message: string;
  duplicateId: string | null;
}

// ============================================================================
// Breakdown Templates
// ============================================================================

// Template stored in the database (snapshot-based)
export interface BreakdownTemplate {
  id: UUID;
  projectType: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  snapshot: BreakdownSnapshot;
  totalCost: number;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

// Input for creating a blank template (rare — most templates created from a job).
export interface BreakdownTemplateCreateInput {
  projectType: string;
  name: string;
  isDefault?: boolean;
}

// Input for creating a template by snapshotting a job's current breakdown.
export interface BreakdownTemplateFromJobInput {
  jobId: UUID;
  projectType: string;
  name: string;
  isDefault?: boolean;
  /**
   * When supplied, the template is built from these editor contents instead of
   * from the job's Active document — captures unsaved edits and the document
   * actually on screen, and carries the rates through.
   */
  contents?: BreakdownDocumentContents;
}

// Input for updating a template's metadata (cannot edit snapshot — it's recreated from a job).
export interface BreakdownTemplateUpdateInput {
  name?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

// ============================================================================
// Common Template Items (shared across all jobs, managed in settings)
// ============================================================================

export interface CommonTemplateItem {
  id: UUID;
  category: string;
  description?: Nullable<string>;
  isReusable: boolean;
  isDisclaimer: boolean;
  sortOrder: number;
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export interface CommonTemplateItemInput {
  id?: UUID;
  category: string;
  description?: string;
  isReusable?: boolean;
  isDisclaimer?: boolean;
  sortOrder: number;
}

export interface CommonTemplateItemsBulkInput {
  items: CommonTemplateItemInput[];
}
